import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAiJobs } from "@/context/AiJobsContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Loader2, AlertTriangle, Layers, Lightbulb, Zap, CalendarRange, User, Euro, FileDown } from "lucide-react";
import { API } from "@/lib/api";

const prioBadge = {
  ALTA: "bg-destructive/10 text-destructive",
  MEDIA: "bg-[#F59E0B]/10 text-[#B45309]",
  BASSA: "bg-[#00A859]/10 text-[#00784A]",
};
const actionBadge = {
  ADOTTARE: "bg-[#00A859] text-white",
  SOSTITUIRE: "bg-destructive text-white",
  CONSOLIDARE: "bg-primary text-white",
  DISMETTERE: "bg-[#F59E0B] text-white",
};

export default function GapAnalysisPage() {
  const { canEdit } = useAuth();
  const { trackJob } = useAiJobs();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const { data: d } = await api.get("/gap-analysis");
    if (d && d.result) { setData(d.result); setMeta({ generated_at: d.generated_at, by: d.generated_by }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const result = await trackJob(api.post("/gap-analysis"), { label: "Gap Analysis AI", link: "/gap-analysis" });
      setData(result.result); setMeta({ generated_at: result.generated_at, by: result.generated_by });
      toast.success("Gap analysis generata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setRunning(false); }
  };

  const health = data?.health_score ?? 0;
  const healthColor = health >= 70 ? "#00A859" : health >= 40 ? "#F59E0B" : "#FF2A2A";

  return (
    <Layout>
      <PageHeader title="Gap Analysis" subtitle="Analisi trasversale dello stack IT">
        {data?.action_plan?.length > 0 && (
          <Button variant="outline" className="rounded-none" data-testid="export-roadmap-btn" onClick={() => window.open(`${API}/gap-analysis/roadmap.pdf`, "_blank")}>
            <FileDown className="w-4 h-4 mr-2" /> Roadmap Q1–Q4 PDF
          </Button>
        )}
        {canEdit && (
          <Button onClick={run} disabled={running} className="rounded-none" data-testid="run-gap-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Genera analisi
          </Button>
        )}
      </PageHeader>

      <div className="p-8">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : !data ? (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <RadarEmpty />
            <p className="text-muted-foreground mt-4">Nessuna gap analysis generata.</p>
            {canEdit ? (
              <Button onClick={run} disabled={running} className="mt-4 rounded-none" data-testid="run-gap-empty-btn">
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Genera la prima analisi
              </Button>
            ) : <p className="text-sm text-muted-foreground mt-2">Chiedi a un amministratore o membro di generarla.</p>}
          </div>
        ) : (
          <div className="space-y-4" data-testid="gap-result">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white border border-border p-6">
                <div className="data-label">Salute dello stack</div>
                <div className="font-mono font-medium text-5xl mt-2" style={{ color: healthColor }} data-testid="health-score">{health}</div>
                <div className="text-xs text-muted-foreground">/ 100</div>
                <div className="w-full h-2 bg-secondary mt-4"><div className="h-2" style={{ width: `${health}%`, background: healthColor }} /></div>
                {meta?.generated_at && <div className="data-label mt-4">Generato: {new Date(meta.generated_at).toLocaleString("it-IT")}</div>}
              </div>
              <div className="lg:col-span-2 bg-white border border-border p-6">
                <div className="data-label mb-2">Sintesi strategica</div>
                <p className="text-sm">{data.summary}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Panel icon={Layers} title="Ridondanze / sovrapposizioni" items={data.redundancies} color="text-[#F59E0B]" />
              <Panel icon={AlertTriangle} title="Lacune (gap)" items={data.gaps} color="text-destructive" />
            </div>

            {data.recommendations?.length > 0 && (
              <div className="bg-white border border-border p-6">
                <div className="flex items-center gap-2 data-label mb-3"><Lightbulb className="w-3.5 h-3.5 text-primary" /> Raccomandazioni</div>
                <div className="border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left">
                      <th className="data-label px-4 py-2">Azione</th>
                      <th className="data-label px-4 py-2">Target</th>
                      <th className="data-label px-4 py-2">Motivazione</th>
                      <th className="data-label px-4 py-2">Priorità</th>
                    </tr></thead>
                    <tbody>
                      {data.recommendations.map((r, i) => (
                        <tr key={i} className={`border-b border-border ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${actionBadge[r.action] || "bg-secondary"}`}>{r.action}</span></td>
                          <td className="px-4 py-2 font-medium">{r.target}</td>
                          <td className="px-4 py-2 text-muted-foreground">{r.reason}</td>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${prioBadge[r.priority] || "bg-secondary"}`}>{r.priority}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.quick_wins?.length > 0 && (
              <div className="bg-white border border-border p-6">
                <div className="flex items-center gap-2 data-label mb-3"><Zap className="w-3.5 h-3.5 text-[#00A859]" /> Quick wins</div>
                <ul className="space-y-1 text-sm list-disc pl-4">{data.quick_wins.map((q, i) => <li key={i}>{q}</li>)}</ul>
              </div>
            )}

            {data.action_plan?.length > 0 && <ActionPlan plan={data.action_plan} />}
          </div>
        )}
      </div>
    </Layout>
  );
}

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const fmtEur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT")}`;

function ActionPlan({ plan }) {
  const totalCost = plan.reduce((s, a) => s + (Number(a.estimated_cost_eur) || 0), 0);
  const totalSav = plan.reduce((s, a) => s + (Number(a.estimated_savings_eur) || 0), 0);
  const grouped = QUARTERS.map((q) => ({ q, items: plan.filter((a) => (a.quarter || "").toUpperCase().includes(q)) }));
  const other = plan.filter((a) => !QUARTERS.some((q) => (a.quarter || "").toUpperCase().includes(q)));

  return (
    <div className="bg-white border border-border p-6" data-testid="action-plan">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 data-label"><CalendarRange className="w-3.5 h-3.5 text-primary" /> Piano d'azione trimestrale</div>
        <div className="flex gap-4 text-sm">
          <span className="text-muted-foreground">Costo: <span className="font-mono font-semibold text-foreground">{fmtEur(totalCost)}</span></span>
          <span className="text-muted-foreground">Risparmio/anno: <span className="font-mono font-semibold text-[#00A859]">{fmtEur(totalSav)}</span></span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {grouped.map(({ q, items }) => (
          <div key={q} className="border border-border">
            <div className="bg-primary text-primary-foreground px-3 py-2 font-heading font-bold text-sm tracking-tight">{q}</div>
            <div className="p-3 space-y-3 min-h-[80px]">
              {items.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
              {items.map((a, i) => (
                <div key={i} className="border-b border-border pb-3 last:border-0 last:pb-0">
                  <div className="font-medium text-sm">{a.title}</div>
                  {a.description && <p className="text-xs text-muted-foreground mt-1">{a.description}</p>}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px]">
                    {a.owner && <span className="flex items-center gap-1 text-muted-foreground"><User className="w-3 h-3" /> {a.owner}</span>}
                    <span className="flex items-center gap-1 font-mono"><Euro className="w-3 h-3" /> {fmtEur(a.estimated_cost_eur)}</span>
                    <span className="flex items-center gap-1 font-mono text-[#00A859]">+{fmtEur(a.estimated_savings_eur)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {other.length > 0 && (
        <div className="mt-3 text-xs text-muted-foreground">Altre iniziative: {other.map((a) => a.title).join(", ")}</div>
      )}
    </div>
  );
}

function Panel({ icon: Icon, title, items, color }) {
  return (
    <div className="bg-white border border-border p-6">
      <div className={`flex items-center gap-2 data-label mb-3 ${color}`}><Icon className="w-3.5 h-3.5" /> {title}</div>
      {items?.length ? (
        <ul className="space-y-1 text-sm list-disc pl-4">{items.map((s, i) => <li key={i}>{s}</li>)}</ul>
      ) : <p className="text-sm text-muted-foreground">Nessun elemento rilevato.</p>}
    </div>
  );
}

function RadarEmpty() {
  return <Sparkles className="w-10 h-10 text-muted-foreground mx-auto" />;
}
